import axios from 'axios';
import { setAlert } from './alert';
import {
    WA_LOADING,
    WA_ERROR,
    GET_WA_OVERVIEW,
    GET_WA_JOURNEYS,
    GET_WA_MESSAGES,
    GET_WA_OFFERS,
    GET_WA_OFFERS_LIST,
    GET_WA_SUPPORT,
    GET_WA_SUPPORT_STATS,
    GET_WA_REASONS,
    GET_WA_TIMELINE
} from './types';

const API_URL = process.env.REACT_APP_SERVER_API_URL;

const ANALYTICS_URL = `${API_URL}/whatsapp-analytics`;

const MESSAGE_FILTER_KEYS = ['journey', 'stage', 'status', 'search'];
const SUPPORT_FILTER_KEYS = ['status', 'type', 'search', 'sort'];
const OFFER_FILTER_KEYS = ['offerKey', 'cycleKey', 'status', 'search'];

// Newest request per dataset wins, so a slow older response never overwrites a newer one
const latestRequest = {};

const pickFilters = (keys, filters = {}) => {
    const params = {};
    keys.forEach(key => {
        if (filters[key] && filters[key] !== 'all') params[key] = filters[key];
    });
    return params;
};

const errorMessage = (err, fallback) => {
    if (err.response?.status === 403) return 'WhatsApp analytics is available to admins only';
    return err.response?.data?.error || fallback;
};

const fetchDataset = ({ url, params, type, fallback, select = res => res.data.data }) => async dispatch => {
    const requestId = (latestRequest[type] || 0) + 1;
    latestRequest[type] = requestId;
    const isStale = () => latestRequest[type] !== requestId;

    try {
        dispatch({ type: WA_LOADING, meta: type });

        const res = await axios.get(url, { params });
        if (isStale()) return;

        if (res.data.success) {
            dispatch({ type, payload: select(res) });
        } else {
            const message = res.data.error || fallback;
            dispatch({ type: WA_ERROR, payload: message, meta: type });
            dispatch(setAlert(message, 'danger'));
        }
    } catch (err) {
        if (isStale()) return;
        const message = errorMessage(err, fallback);
        console.error(fallback, err);
        dispatch({ type: WA_ERROR, payload: message, meta: type });
        dispatch(setAlert(message, 'danger'));
    }
};

const withPagination = res => ({
    rows: res.data.data,
    total: res.data.pagination.total,
    pages: res.data.pagination.pages,
    counts: res.data.counts
});

/**
 * Headline KPIs, daily trend and failure reasons
 * @param {Object} range
 * @param {string} range.from - IST day (YYYY-MM-DD)
 * @param {string} range.to - IST day (YYYY-MM-DD)
 */
export const getWhatsappOverview = ({ from, to }) => fetchDataset({
    url: `${ANALYTICS_URL}/overview`,
    params: { from, to },
    type: GET_WA_OVERVIEW,
    fallback: 'Failed to fetch WhatsApp overview'
});

/**
 * Per-journey stage funnels with taps and conversions
 * @param {Object} range - { from, to } IST days
 */
export const getWhatsappJourneys = ({ from, to }) => fetchDataset({
    url: `${ANALYTICS_URL}/journeys`,
    params: { from, to },
    type: GET_WA_JOURNEYS,
    fallback: 'Failed to fetch WhatsApp journeys'
});

/**
 * Page of the message log with server-side filters
 * @param {Object} filters
 * @param {string} [filters.from] - IST day (YYYY-MM-DD)
 * @param {string} [filters.to] - IST day (YYYY-MM-DD)
 * @param {string} [filters.journey]
 * @param {string} [filters.stage]
 * @param {string} [filters.status]
 * @param {string} [filters.search] - Customer name or phone
 * @param {number} [filters.page] - Page number
 * @param {number} [filters.limit] - Page size
 */
export const getWhatsappMessages = ({ page = 1, limit = 20, from, to, ...filters } = {}) => fetchDataset({
    url: `${ANALYTICS_URL}/messages`,
    params: { page, limit, ...(from && to ? { from, to } : {}), ...pickFilters(MESSAGE_FILTER_KEYS, filters) },
    type: GET_WA_MESSAGES,
    fallback: 'Failed to fetch WhatsApp messages',
    select: withPagination
});

/**
 * Offers made in the range, grouped by offer and cycle
 * @param {Object} range - { from, to } IST days
 */
export const getWhatsappOffers = ({ from, to }) => fetchDataset({
    url: `${ANALYTICS_URL}/offers`,
    params: { from, to },
    type: GET_WA_OFFERS,
    fallback: 'Failed to fetch WhatsApp offers'
});

/**
 * Page of individual offers made in the range
 * @param {Object} filters
 * @param {string} filters.from - IST day (YYYY-MM-DD)
 * @param {string} filters.to - IST day (YYYY-MM-DD)
 * @param {string} [filters.offerKey] - welcome_10_2, pack_50 or pack_100
 * @param {string} [filters.cycleKey] - Pack month (YYYY-MM)
 * @param {string} [filters.status]
 * @param {string} [filters.search] - Customer name, phone or user id
 * @param {number} [filters.page] - Page number
 * @param {number} [filters.limit] - Page size
 */
export const getWhatsappOffersList = ({ page = 1, limit = 20, from, to, ...filters } = {}) => fetchDataset({
    url: `${ANALYTICS_URL}/offers/list`,
    params: { page, limit, from, to, ...pickFilters(OFFER_FILTER_KEYS, filters) },
    type: GET_WA_OFFERS_LIST,
    fallback: 'Failed to fetch WhatsApp offers list',
    select: withPagination
});

/**
 * Support request counts by type and status plus the open backlog
 * @param {Object} range - { from, to } IST days
 */
export const getWhatsappSupportStats = ({ from, to }) => fetchDataset({
    url: `${ANALYTICS_URL}/support-stats`,
    params: { from, to },
    type: GET_WA_SUPPORT_STATS,
    fallback: 'Failed to fetch WhatsApp support stats'
});

/**
 * Day 25 and Day 30 reason distributions
 * @param {Object} range - { from, to } IST days
 */
export const getWhatsappReasons = ({ from, to }) => fetchDataset({
    url: `${ANALYTICS_URL}/reasons`,
    params: { from, to },
    type: GET_WA_REASONS,
    fallback: 'Failed to fetch WhatsApp reasons'
});

/**
 * Page of the support queue
 * @param {Object} filters
 * @param {string} [filters.status] - open, in_progress or closed tab
 * @param {string} [filters.type]
 * @param {string} [filters.search] - Customer name, phone or user id
 * @param {string} [filters.sort] - newest (default) or oldest
 * @param {number} [filters.page] - Page number
 * @param {number} [filters.limit] - Page size
 */
export const getWhatsappSupport = ({ page = 1, limit = 20, ...filters } = {}) => fetchDataset({
    url: `${API_URL}/whatsapp-support`,
    params: { page, limit, ...pickFilters(SUPPORT_FILTER_KEYS, filters) },
    type: GET_WA_SUPPORT,
    fallback: 'Failed to fetch WhatsApp support requests',
    select: withPagination
});

/**
 * Change the status and/or notes of one support request
 * @param {string} id - Support request id
 * @param {Object} changes
 * @param {string} [changes.status] - open, in_progress, resolved or dismissed
 * @param {string} [changes.notes] - Free text up to 1000 characters
 * @returns {Promise<Object|null>} updated request row, or null after an alert on failure
 */
export const updateWhatsappSupport = (id, changes) => async dispatch => {
    try {
        const res = await axios.put(`${API_URL}/whatsapp-support/${id}`, changes);
        if (res.data.success) return res.data.data;
        dispatch(setAlert(res.data.error || 'Failed to update support request', 'danger'));
        return null;
    } catch (err) {
        console.error('Failed to update support request', err);
        dispatch(setAlert(errorMessage(err, 'Failed to update support request'), 'danger'));
        return null;
    }
};

/**
 * Merged chronological history of one customer
 * @param {string} userId
 */
export const getWhatsappTimeline = (userId) => fetchDataset({
    url: `${ANALYTICS_URL}/users/${userId}/timeline`,
    type: GET_WA_TIMELINE,
    fallback: 'Failed to fetch WhatsApp timeline'
});

/**
 * Rows for an xlsx export, built by the caller. Not stored in state.
 * @param {string} type - messages, events, offers or support
 * @param {Object} range - { from, to } IST days
 * @returns {Promise<Array|null>} rows (empty when the range has none), or null after an alert on failure
 */
export const exportWhatsappData = (type, { from, to }) => async dispatch => {
    try {
        const res = await axios.get(`${ANALYTICS_URL}/export`, { params: { type, from, to } });
        if (res.data.success) return res.data.data;
        dispatch(setAlert(res.data.error || 'Failed to export WhatsApp data', 'danger'));
        return null;
    } catch (err) {
        dispatch(setAlert(errorMessage(err, 'Failed to export WhatsApp data'), 'danger'));
        return null;
    }
};
